import type { CommandContext, CommandHandler } from './chatCommands';
import { executeReducerByCommand, getReducerUsage, listReducerSignatures } from './reducerCommands';

interface CommandDefinition {
  name: string;
  usage: string;
  description: string;
  handler: CommandHandler;
}

export interface ServerCommandDefinition {
  name: string;
  usage: string;
  description: string;
}

const commandDefinitions: CommandDefinition[] = [];
const commandHandlers = new Map<string, CommandHandler>();
let serverCommandDefinitions: ServerCommandDefinition[] = [];

function registerCommand(definition: CommandDefinition): void {
  commandDefinitions.push(definition);
  commandHandlers.set(definition.name.toLowerCase(), definition.handler);
}

export function getCommandHandler(commandName: string): CommandHandler | undefined {
  return commandHandlers.get(commandName.toLowerCase());
}

function listCommandHelpLines(): string[] {
  const localLines = commandDefinitions.map((definition) => `${definition.usage} - ${definition.description}`);
  const serverLines = serverCommandDefinitions.map((definition) => `${definition.usage} - ${definition.description}`);
  return [...localLines, ...serverLines];
}

export function setServerCommandDefinitions(definitions: ServerCommandDefinition[]): void {
  serverCommandDefinitions = [...definitions].sort((left, right) => left.usage.localeCompare(right.usage));
}

registerCommand({
  name: 'help',
  usage: '/help',
  description: 'List available chat commands',
  handler: (_args, ctx) => {
    const lines = listCommandHelpLines();
    ctx.showLocalMessage(lines.join('\n'));
    return { handled: true };
  },
});

registerCommand({
  name: 'reducers',
  usage: '/reducers',
  description: 'List client-known reducers and their arguments',
  handler: (_args, ctx) => {
    ctx.showLocalMessage(listReducerSignatures().join('\n'));
    return { handled: true };
  },
});

registerCommand({
  name: 'reducer',
  usage: '/reducer [name] [args...]',
  description: 'Invoke a reducer locally by name',
  handler: (args, ctx) => {
    const reducerName = args[0];
    if (!reducerName) {
      return {
        handled: true,
        error: 'Usage: /reducer [name] [args...]',
      };
    }

    void executeReducerByCommand(ctx.spaceTimeClient, reducerName, args.slice(1))
      .then(() => {
        ctx.showLocalMessage(`Reducer sent: ${getReducerUsage(reducerName)}`);
      })
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : `Failed to invoke reducer ${reducerName}`;
        ctx.showLocalMessage(message);
      });

    return { handled: true };
  },
});

registerCommand({
  name: 'w',
  usage: '/w [player] message',
  description: 'Whisper a player',
  handler: (args, ctx) => {
    if (args.length === 0) {
      return { handled: true, error: 'Usage: /w [player] message' };
    }

    const cached = ctx.getLastWhisperTarget();
    let target: string;
    let message: string;

    if (args.length === 1 && cached) {
      target = cached;
      message = args[0];
    } else if (args.length >= 2) {
      target = args[0];
      message = args.slice(1).join(' ');
    } else if (args.length === 1 && !cached) {
      return { handled: true, error: 'Usage: /w player message' };
    } else {
      return { handled: true, error: 'Usage: /w [player] message' };
    }

    if (!message) {
      return { handled: true, error: 'Message cannot be empty' };
    }

    ctx.setLastWhisperTarget(target);
    return {
      handled: true,
      channel: { tag: 'Whisper', value: target },
      text: message,
    };
  },
});

registerCommand({
  name: 'r',
  usage: '/r message',
  description: 'Reply to the last whisper sender',
  handler: (args, ctx) => {
    const sender = ctx.getLastWhisperSender();
    if (!sender) {
      return { handled: true, error: 'No one to reply to' };
    }

    const message = args.join(' ');
    if (!message) {
      return { handled: true, error: 'Usage: /r message' };
    }

    ctx.setLastWhisperTarget(sender);
    return {
      handled: true,
      channel: { tag: 'Whisper', value: sender },
      text: message,
    };
  },
});
